import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldAlert,
  CheckCircle2,
  Scale,
  BookOpen,
  Wrench,
  Quote,
  Sparkles,
} from "lucide-react";
import type { StructuredEvaluationDetails } from "../../gen/models";
import { EntityLinkedText } from "./EntityLinkedText";

interface StructuredEvaluationViewProps {
  details: StructuredEvaluationDetails;
}

export const StructuredEvaluationView: React.FC<StructuredEvaluationViewProps> = ({
  details,
}) => {
  const ruleEvals = details.rule_evaluations || [];
  const knowledgeCitations = details.knowledge_citations || [];
  const dimensionCritiques = details.dimension_critiques || {};
  const entityRefs = details.entity_references || [];

  const violations = ruleEvals.filter((r) => r.status === "violated");
  const adhered = ruleEvals.filter((r) => r.status === "adhered");
  const toolEfficiency = dimensionCritiques["tool_efficiency"];

  const getSeverityBadge = (severity?: string) => {
    switch (severity?.toLowerCase()) {
      case "critical":
        return (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-800">
            Critical
          </span>
        );
      case "major":
        return (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 dark:bg-amber-950/80 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
            Major
          </span>
        );
      case "minor":
        return (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-sky-100 dark:bg-sky-950/80 text-sky-700 dark:text-sky-300 border border-sky-300 dark:border-sky-800">
            Minor
          </span>
        );
      default:
        return (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
            {severity || "Info"}
          </span>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Executive Summary */}
      {details.summary && (
        <div className="p-3.5 rounded-xl bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800 shadow-2xs">
          <div className="flex items-center gap-1.5 text-xs font-bold text-stone-900 dark:text-stone-100 mb-1.5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            <span>Executive Summary</span>
          </div>
          <p className="text-xs text-stone-700 dark:text-stone-300 leading-relaxed">
            <EntityLinkedText text={details.summary} />
          </p>
        </div>
      )}

      {/* Rule Violations */}
      {violations.length > 0 && (
        <div className="space-y-2.5">
          <div className="flex items-center gap-1.5 text-xs font-bold text-rose-700 dark:text-rose-400">
            <ShieldAlert className="w-4 h-4" />
            <span>Rule Violations ({violations.length})</span>
          </div>
          <div className="space-y-2.5">
            {violations.map((violation, idx) => {
              const ruleId = violation.rule_id;
              const ruleTitle = violation.rule_title || `Rule [${ruleId.slice(0, 8)}...]`;
              return (
                <div
                  key={`${ruleId}-${idx}`}
                  className="p-3 rounded-xl bg-rose-50/70 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to={`/rules/${ruleId}`}
                      className="group flex items-center gap-1.5 text-xs font-bold text-stone-900 dark:text-stone-100 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                      title={`Open Rule ${ruleId}`}
                    >
                      <Scale className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 shrink-0" />
                      <span className="underline decoration-stone-300 dark:decoration-stone-700 group-hover:decoration-emerald-500">
                        {ruleTitle}
                      </span>
                    </Link>
                    {getSeverityBadge(violation.severity)}
                  </div>

                  {violation.violated_quote && (
                    <div className="flex items-start gap-2 p-2 rounded-lg bg-white/80 dark:bg-stone-900/80 border border-rose-100 dark:border-rose-950 text-[11px] text-stone-700 dark:text-stone-300 font-mono">
                      <Quote className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
                      <span className="italic leading-snug">"{violation.violated_quote}"</span>
                    </div>
                  )}

                  <p className="text-xs text-stone-700 dark:text-stone-300 leading-relaxed">
                    <EntityLinkedText text={violation.explanation} />
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Adhered Rules */}
      {adhered.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="w-4 h-4" />
            <span>Rules Adhered ({adhered.length})</span>
          </div>
          <div className="space-y-1.5">
            {adhered.map((item, idx) => {
              const ruleId = item.rule_id;
              const ruleTitle = item.rule_title || `Rule [${ruleId.slice(0, 8)}...]`;
              return (
                <div
                  key={`${ruleId}-${idx}`}
                  className="p-2.5 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40 flex items-start justify-between gap-2 text-xs"
                >
                  <Link
                    to={`/rules/${ruleId}`}
                    className="group inline-flex items-center gap-1.5 font-medium text-stone-800 dark:text-stone-200 hover:text-emerald-600 dark:hover:text-emerald-400"
                  >
                    <CheckCircle2 className="w-3 h-3 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <span className="hover:underline">{ruleTitle}</span>
                  </Link>
                  {item.explanation && (
                    <span className="text-[11px] text-stone-500 dark:text-stone-400 text-right truncate max-w-[200px]">
                      {item.explanation}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Knowledge Citations & Contradictions */}
      {knowledgeCitations.length > 0 && (
        <div className="space-y-2.5">
          <div className="flex items-center gap-1.5 text-xs font-bold text-stone-900 dark:text-stone-100">
            <BookOpen className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
            <span>Knowledge Verification ({knowledgeCitations.length})</span>
          </div>
          <div className="space-y-2.5">
            {knowledgeCitations.map((citation, idx) => {
              const isContradiction = citation.status === "contradicted";
              const knowledgeId = citation.knowledge_id;
              const title = citation.knowledge_title || "Knowledge Item";
              return (
                <div
                  key={`${knowledgeId}-${idx}`}
                  className={`p-3 rounded-xl border space-y-2 ${
                    isContradiction
                      ? "bg-amber-50/70 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/60"
                      : "bg-stone-50 dark:bg-stone-900 border-stone-200 dark:border-stone-800"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    {knowledgeId ? (
                      <Link
                        to={`/knowledge/${knowledgeId}`}
                        className="group flex items-center gap-1.5 text-xs font-bold text-stone-900 dark:text-stone-100 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
                      >
                        <BookOpen className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400 shrink-0" />
                        <span className="underline decoration-stone-300 dark:decoration-stone-700 group-hover:decoration-cyan-500">
                          {title}
                        </span>
                      </Link>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs font-bold text-stone-900 dark:text-stone-100">
                        <BookOpen className="w-3.5 h-3.5 text-stone-400" />
                        <span>{title}</span>
                      </div>
                    )}
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                        isContradiction
                          ? "bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800"
                          : "bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800"
                      }`}
                    >
                      {citation.status}
                    </span>
                  </div>

                  {(citation.claim || citation.ground_truth) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                      {citation.claim && (
                        <div className="p-2 rounded-lg bg-white/70 dark:bg-stone-950/70 border border-stone-200 dark:border-stone-800 space-y-0.5">
                          <span className="font-semibold text-rose-600 dark:text-rose-400 block text-[10px] uppercase">
                            Agent Claim
                          </span>
                          <span className="text-stone-700 dark:text-stone-300 italic">
                            "{citation.claim}"
                          </span>
                        </div>
                      )}
                      {citation.ground_truth && (
                        <div className="p-2 rounded-lg bg-white/70 dark:bg-stone-950/70 border border-stone-200 dark:border-stone-800 space-y-0.5">
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400 block text-[10px] uppercase">
                            Ground Truth
                          </span>
                          <span className="text-stone-700 dark:text-stone-300">
                            {citation.ground_truth}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {citation.explanation && (
                    <p className="text-xs text-stone-700 dark:text-stone-300 leading-relaxed">
                      <EntityLinkedText text={citation.explanation} />
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tool Efficiency Breakdown */}
      {toolEfficiency && (
        <div className="p-3 rounded-xl bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800 space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-bold text-stone-900 dark:text-stone-100">
              <Wrench className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              <span>Tool Efficiency Breakdown</span>
            </div>
            <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-stone-200 dark:bg-stone-800 text-stone-800 dark:text-stone-200">
              {toolEfficiency.score.toFixed(1)} / 5.0
            </span>
          </div>
          <p className="text-xs text-stone-700 dark:text-stone-300 leading-relaxed">
            <EntityLinkedText text={toolEfficiency.rationale} />
          </p>
        </div>
      )}

      {/* Entity References Quick Links */}
      {entityRefs.length > 0 && (
        <div className="pt-2 border-t border-stone-100 dark:border-stone-800">
          <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400 block mb-1.5">
            Referenced Entities
          </span>
          <div className="flex flex-wrap gap-1.5">
            {entityRefs.map((ref, idx) => {
              const url = ref.target_path || (ref.entity_type === "rule" ? `/rules/${ref.entity_id}` : `/knowledge/${ref.entity_id}`);
              const Icon = ref.entity_type === "rule" ? Scale : BookOpen;
              const title = ref.title || `${ref.entity_type} [${ref.entity_id.slice(0, 8)}]`;
              return (
                <Link
                  key={`${ref.entity_id}-${idx}`}
                  to={url}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-stone-700 dark:text-stone-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline transition-colors"
                >
                  <Icon className="w-3 h-3 text-stone-500" />
                  <span>{title}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
